import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import { HomePage } from "./pages/HomePage";
import { DayPage } from "./pages/DayPage";
import { SubmitEventPage } from "./pages/SubmitEventPage";
import { AdminApp } from "./admin/AdminApp";

async function forceReloadWithoutCaches() {
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set("__reload", String(Date.now()));
  let navigated = false;
  try {
    const controller = navigator.serviceWorker?.controller;
    controller?.postMessage({ type: "CLEAR_CACHES" });
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.update().catch(() => undefined)));
    }
  } catch {
    /* リロード優先 */
  } finally {
    navigated = true;
    window.location.assign(nextUrl.toString());
    window.setTimeout(() => {
      if (navigated) window.location.reload();
    }, 900);
  }
}

function PullToRefresh() {
  const [state, setState] = useState<"idle" | "pulling" | "ready" | "refreshing" | "done">(() => {
    if (sessionStorage.getItem("pull_refresh_done_v1") === "1") {
      sessionStorage.removeItem("pull_refresh_done_v1");
      return "done";
    }
    return "idle";
  });

  useEffect(() => {
    if (state === "done") {
      const t = window.setTimeout(() => setState("idle"), 1300);
      return () => window.clearTimeout(t);
    }
  }, [state]);

  useEffect(() => {
    let startY = 0;
    let armed = false;
    let hideTimer: number | undefined;
    const isInteractive = (target: EventTarget | null) => {
      const el = target instanceof Element ? target : null;
      return Boolean(el?.closest("input, textarea, select, button, a, [role='button']"));
    };
    const resetSoon = () => {
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setState("idle"), 180);
    };
    const isAtPageTop = () => {
      const top = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      return top <= 8;
    };
    const onStart = (ev: TouchEvent) => {
      if (!isAtPageTop() || isInteractive(ev.target)) {
        armed = false;
        setState("idle");
        return;
      }
      startY = ev.touches[0]?.clientY ?? 0;
      armed = true;
    };
    const onMove = (ev: TouchEvent) => {
      if (!armed || !isAtPageTop()) return;
      const y = ev.touches[0]?.clientY ?? startY;
      const diff = y - startY;
      if (diff > 55) setState("ready");
      else if (diff > 18) setState("pulling");
    };
    const onEnd = (ev: TouchEvent) => {
      if (!armed) return;
      armed = false;
      const endY = ev.changedTouches[0]?.clientY ?? startY;
      if (endY - startY > 55 && isAtPageTop()) {
        setState("refreshing");
        sessionStorage.setItem("pull_refresh_done_v1", "1");
        window.setTimeout(() => void forceReloadWithoutCaches(), 120);
      } else {
        resetSoon();
      }
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.clearTimeout(hideTimer);
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, []);

  if (state === "idle") return null;
  return (
    <div className={`pull-refresh-indicator pull-refresh-indicator--${state}`} role="status" aria-live="polite">
      <span className="pull-refresh-spinner" aria-hidden />
      <span>{state === "ready" ? "離すとリロードします" : state === "refreshing" ? "リロード中…" : state === "done" ? "リロード完了しました！" : "下に引っぱるとリロード"}</span>
    </div>
  );
}

export function App() {
  return (
    <>
      <PullToRefresh />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/submit" element={<SubmitEventPage />} />
        <Route path="/day/:date" element={<DayPage />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
